import { MoneyDisplay } from "../components/MoneyDisplay";
import { StatusPill } from "../components/StatusPill";
import { Money } from "../../../shared/money/Money";
import { Decimal } from "../../../shared/money/Decimal";
import { formatMoney } from "../lib/format";
import { DownloadIcon } from "../components/icons";
import { useState } from "preact/hooks";
import { AccountSwitcher } from "../features/accounts/AccountSwitcher";
import type { PortalAccount } from "../store/portalAuthApi";

export function StyleGuidePage() {
  return (
    <section class="style-guide" aria-label="GasBill Online style guide">
      <header class="style-guide__intro">
        <h2>GasBill Online style guide</h2>
        <p>
          A calm utility checkout system that helps payers confirm the tenant, understand what is
          due, and complete payment without visual noise.
        </p>
      </header>

      <nav class="style-guide__nav" aria-label="Style guide sections">
        <a href="/style-guide#principles">Principles</a>
        <a href="/style-guide#tokens">Tokens</a>
        <a href="/style-guide#checkout">Checkout</a>
        <a href="/style-guide#components">Components</a>
        <a href="/style-guide#states">States</a>
      </nav>

      <section class="style-guide__section" aria-label="Account displays">
        <h3>Account displays</h3>
        <p class="muted">
          Exact money values retain their precision. Use the large size for the account balance.
        </p>
        <MoneyDisplay value={Money.create(Decimal.parse("412.80"))} size="large" />
        <div>
          <StatusPill>Open</StatusPill> <StatusPill tone="warning">Partially paid</StatusPill>{" "}
          <StatusPill tone="success">Paid</StatusPill>
        </div>
      </section>
      <section id="principles" class="style-guide__section">
        <div>
          <h3>Trust through clarity</h3>
        </div>
        <div class="principle-grid">
          <article class="guide-card">
            <strong>Payment first</strong>
            <p>
              The balance, payment amount, confirmation, and receipt actions get the strongest
              hierarchy.
            </p>
          </article>
          <article class="guide-card">
            <strong>Tenant reassurance</strong>
            <p>
              The tenant logo confirms the payer is in the right place; branding should never feel
              promotional.
            </p>
          </article>
          <article class="guide-card">
            <strong>Semantic color</strong>
            <p>
              Use the accent for primary actions, links, selection, and focus. Reserve green for
              successful payment or paid-in-full states.
            </p>
          </article>
          <article class="guide-card">
            <strong>Headings carry the label</strong>
            <p>
              Name things in the heading itself ("Invoice INV-123"). Don't stack small uppercase
              eyebrows above headings, and write field labels in sentence case.
            </p>
          </article>
          <article class="guide-card">
            <strong>Rows are the action</strong>
            <p>
              When a list item opens something, make the whole row the link. Keep extra controls in
              a row to one clear icon action, such as download.
            </p>
          </article>
        </div>
      </section>

      <section id="tokens" class="style-guide__section">
        <div>
          <h3>Three color tiers</h3>
          <p class="muted style-guide__lede">
            The core palette holds raw values and is never used by components directly. Semantic
            roles stay the same for every tenant. Accent roles are the only tokens a tenant brand
            color may override later.
          </p>
        </div>

        <TokenGroup title="Semantic roles" swatches={semanticSwatches} />
        <TokenGroup title="Status" swatches={statusSwatches} />
        <TokenGroup title="Accent roles" swatches={accentSwatches} />

        <div class="token-group">
          <h4>Type scale</h4>
          <p class="muted">
            Inter, bundled with the app. Headings are semibold, and large amounts use the medium
            display weight with tabular figures so columns line up.
          </p>
          <div class="type-scale">
            {typeSteps.map((step) => (
              <div class="type-scale__row" key={step.varName}>
                <code>{step.varName}</code>
                <span style={{ fontSize: `var(${step.varName})`, fontWeight: step.weight }}>
                  {step.sample}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div class="token-group">
          <h4>Spacing</h4>
          <p class="muted">
            A 4px grid. Lay out siblings with <code>gap</code> rather than per-element margins.
          </p>
          <div class="space-scale">
            {spaceSteps.map((varName) => (
              <div class="space-scale__row" key={varName}>
                <code>{varName}</code>
                <span class="space-scale__bar" style={{ width: `var(${varName})` }}></span>
              </div>
            ))}
          </div>
        </div>

        <div class="token-group">
          <h4>Shape and elevation</h4>
          <div class="shape-grid">
            <div class="shape-sample" style={{ borderRadius: "var(--radius-sm)" }}>
              <code>--radius-sm</code>
              <span>Inputs, options, notices</span>
            </div>
            <div class="shape-sample" style={{ borderRadius: "var(--radius-md)" }}>
              <code>--radius-md</code>
              <span>Cards</span>
            </div>
            <div class="shape-sample" style={{ borderRadius: "var(--radius-lg)" }}>
              <code>--radius-lg</code>
              <span>Balance card, payment rail</span>
            </div>
            <div class="shape-sample" style={{ borderRadius: "var(--radius-pill)" }}>
              <code>--radius-pill</code>
              <span>Buttons, badges, tabs</span>
            </div>
            <div
              class="shape-sample"
              style={{
                borderRadius: "var(--radius-md)",
                border: 0,
                boxShadow: "var(--shadow-card)",
              }}
            >
              <code>--shadow-card</code>
              <span>Cards, instead of borders</span>
            </div>
            <div
              class="shape-sample"
              style={{
                borderRadius: "var(--radius-lg)",
                border: 0,
                boxShadow: "var(--shadow-raised)",
              }}
            >
              <code>--shadow-raised</code>
              <span>Balance card, payment rail</span>
            </div>
            <div
              class="shape-sample"
              style={{
                borderRadius: "var(--radius-md)",
                border: 0,
                boxShadow: "var(--shadow-overlay)",
              }}
            >
              <code>--shadow-overlay</code>
              <span>Menus, dialogs</span>
            </div>
          </div>
        </div>
      </section>

      <section id="checkout" class="style-guide__section">
        <div>
          <h3>Context left, payment right</h3>
        </div>
        <div class="checkout-layout style-guide__checkout-example">
          <div class="checkout-main">
            <section class="invoice-document">
              <header class="document-header">
                <h2>Invoice INV-123</h2>
                <button class="secondary-action icon-action document-download" type="button">
                  <DownloadIcon class="button-icon" />
                  <span>Download</span>
                </button>
              </header>
              <dl class="document-facts">
                <div>
                  <dt>Bill to</dt>
                  <dd>Test Customer</dd>
                  <dd class="muted">100 Main St, Omaha, NE 68102</dd>
                </div>
                <div class="date-facts">
                  <div>
                    <dt>Issued</dt>
                    <dd>May 1, 2026</dd>
                  </div>
                  <div>
                    <dt>Due</dt>
                    <dd>May 31, 2026</dd>
                  </div>
                </div>
              </dl>
            </section>
            <section class="invoice-statement">
              <div class="line-item">
                <div>
                  <strong>Propane delivery</strong>
                  <span>100 x $2.00 · $12.50 tax</span>
                </div>
                <div class="item-amount">
                  <strong>$200.00</strong>
                </div>
              </div>
              <div class="line-item">
                <div>
                  <strong>Payment received</strong>
                  <span>May 10, 2026</span>
                </div>
                <div class="item-amount">
                  <strong>-$50.00</strong>
                  <button class="link-action receipt-link" type="button">
                    Download receipt
                  </button>
                </div>
              </div>
            </section>
          </div>
          <aside class="checkout-rail" aria-label="Payment summary">
            <div class="payment-due-card">
              <span>Balance due</span>
              <strong>{formatMoney(162.5)}</strong>
            </div>
            <section class="payment-panel" aria-label="Payment">
              <div class="payment-amount-form">
                <div class="payment-step-summary">
                  <span>Payment amount</span>
                  <strong>{formatMoney(162.5)}</strong>
                </div>
                <button class="primary-action" type="button">
                  Continue to card details
                </button>
              </div>
            </section>
          </aside>
        </div>
      </section>

      <section id="components" class="style-guide__section">
        <div>
          <h3>Core controls</h3>
        </div>
        <div class="component-grid">
          <article class="guide-card">
            <h4>Tenant identity</h4>
            <div class="tenant-identity">
              <div class="tenant-logo-example">Tenant logo</div>
            </div>
          </article>
          <article class="guide-card">
            <h4>Actions</h4>
            <div class="guide-actions">
              <button class="primary-action" type="button">
                Pay $162.50
              </button>
              <button class="secondary-action" type="button">
                Back
              </button>
              <button class="link-action" type="button">
                Change
              </button>
            </div>
          </article>
          <article class="guide-card">
            <h4>Form field</h4>
            <label class="form-control">
              <span>Invoice number</span>
              <input value="INV-123" readOnly />
            </label>
          </article>
          <article class="guide-card" id="account-switcher">
            <h4>Account switcher</h4>
            <AccountSwitcherExample />
            <p class="muted">
              The pill opens account rows with the address and balance. The current account has a
              check. Use Tab or arrow keys to move, Enter to select, and Escape to close. A single
              selected account is plain text.
            </p>
          </article>
        </div>
      </section>

      <section id="states" class="style-guide__section">
        <div>
          <h3>Payment feedback</h3>
        </div>
        <div class="component-grid">
          <div class="paid-state" role="status">
            <strong>No payment due</strong>
            <span>This invoice is paid in full.</span>
          </div>
          <div class="payment-success" role="status">
            <strong>Payment successful</strong>
            <span>$100.00 applied to account balance.</span>
          </div>
          <p class="payment-error" role="alert">
            Payment information could not be validated.
          </p>
          <div class="payment-disclosure">
            Credit card payments may include a surcharge. Debit and prepaid cards are not
            surcharged.
          </div>
        </div>
      </section>
    </section>
  );
}

const exampleAccounts: PortalAccount[] = [
  {
    customerId: "farm",
    matchedEmails: [],
    name: "Miller Farm",
    code: "MILL04",
    serviceAddress: "118 County Rd 9",
    balance: 412.8,
  },
  {
    customerId: "grain",
    matchedEmails: [],
    name: "Miller Grain LLC",
    code: "MILL07",
    serviceAddress: "4410 Highway 14",
    balance: 0,
  },
  {
    customerId: "jean",
    matchedEmails: [],
    name: "Jean Miller",
    code: "JMIL01",
    serviceAddress: "22 Elm St",
    balance: 86.2,
  },
];

function AccountSwitcherExample() {
  const [currentCustomerId, setCurrentCustomerId] = useState("farm");
  return (
    <AccountSwitcher
      accounts={exampleAccounts}
      currentCustomerId={currentCustomerId}
      onSelect={async (customerId) => setCurrentCustomerId(customerId)}
    />
  );
}

type Swatch = { name: string; varName: string };

const semanticSwatches: Swatch[] = [
  { name: "Page", varName: "--color-page" },
  { name: "Surface", varName: "--color-surface" },
  { name: "Surface subtle", varName: "--color-surface-subtle" },
  { name: "Surface sunken", varName: "--color-surface-sunken" },
  { name: "Border", varName: "--color-border" },
  { name: "Border strong", varName: "--color-border-strong" },
  { name: "Text", varName: "--color-text" },
  { name: "Muted text", varName: "--color-text-muted" },
];

const statusSwatches: Swatch[] = [
  { name: "Success", varName: "--color-success" },
  { name: "Success surface", varName: "--color-success-surface" },
  { name: "Warning", varName: "--color-warning" },
  { name: "Warning surface", varName: "--color-warning-surface" },
  { name: "Danger", varName: "--color-danger" },
  { name: "Danger surface", varName: "--color-danger-surface" },
  { name: "Info", varName: "--color-info" },
  { name: "Info surface", varName: "--color-info-surface" },
];

const accentSwatches: Swatch[] = [
  { name: "Solid", varName: "--accent-solid" },
  { name: "Solid hover", varName: "--accent-solid-hover" },
  { name: "Solid disabled", varName: "--accent-solid-disabled" },
  { name: "On solid", varName: "--accent-on-solid" },
  { name: "Soft", varName: "--accent-soft" },
  { name: "Text", varName: "--accent-text" },
  { name: "Focus ring", varName: "--accent-focus-ring" },
];

const typeSteps = [
  { varName: "--text-display", weight: 600, sample: "Invoice INV-20481" },
  { varName: "--text-3xl", weight: 500, sample: formatMoney(1186.4) },
  { varName: "--text-2xl", weight: 600, sample: "Account overview" },
  { varName: "--text-xl", weight: 600, sample: "Open invoices" },
  { varName: "--text-lg", weight: 600, sample: "Pay account balance" },
  { varName: "--text-md", weight: 400, sample: "Your payment was applied to invoice INV-20481." },
  { varName: "--text-sm", weight: 400, sample: "Credit card payments include a 3% surcharge." },
  { varName: "--text-xs", weight: 400, sample: "Read 3 hours ago" },
];

const spaceSteps = [
  "--space-1",
  "--space-2",
  "--space-3",
  "--space-4",
  "--space-5",
  "--space-6",
  "--space-7",
  "--space-8",
];

function TokenGroup({ title, swatches }: { title: string; swatches: Swatch[] }) {
  return (
    <div class="token-group">
      <h4>{title}</h4>
      <div class="token-grid">
        {swatches.map((swatch) => (
          <TokenSwatch key={swatch.varName} {...swatch} />
        ))}
      </div>
    </div>
  );
}

function TokenSwatch({ name, varName }: Swatch) {
  return (
    <article class="token-swatch">
      <div style={{ background: `var(${varName})` }}></div>
      <strong>{name}</strong>
      <span>{varName}</span>
    </article>
  );
}
